import { useCallback } from 'react';
import { compact } from 'lodash';

import { VideoCall, VideoCallQuery } from '@dreryk/egabinet-video-call-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { AppointmentDetails, AppointmentDetailsQuery } from '@dreryk/egabinet-appointment-frontend-api';

import {
   dispatchSnackbarNegativeEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   StyledFlex,
   Tooltip,
   VideoProfileIcon,
} from '@dreryk/react-components';
import { TranslatableText, Translate } from '@dreryk/react-common';
import { performCqrsQueryFind, performCqrsQueryGet } from '@dreryk/command-query-react-common';

import { translationKeys } from '../translations';
import { useVideoConsultationGlobalState } from '../shared/useVideoConsultationGlobalState';

import { StyledVideoConsultationStartButton } from './VideoConsultationStartButton.styled';

type VideoConsultationStartButtonProps = Readonly<{
   appointmentId: number;
   label: TranslatableText;
   disabled?: boolean;
}>;

export const VideoConsultationStartButton = (props: VideoConsultationStartButtonProps): JSX.Element => {
   const { call } = useVideoConsultationGlobalState();
   const handleClick = useCallback(async () => {
      const videoCall = await performCqrsQueryFind<VideoCall>(() => {
         const query = new VideoCallQuery();
         query.appointmentId = AppointmentId.of(props.appointmentId);
         return query;
      });

      if (!videoCall) {
         return dispatchSnackbarNegativeEvent(translationKeys.failedToFetchVideoConsultationData);
      }

      const { patient } = await performCqrsQueryGet<AppointmentDetails>(() => {
         const query = new AppointmentDetailsQuery();
         query.appointmentId = AppointmentId.of(props.appointmentId);
         return query;
      });

      return call({
         participantName: compact([patient.firstName, patient.lastName]).join(' '),
         roomName: videoCall.roomName,
         token: videoCall.token,
         originId: props.appointmentId,
      });
   }, [call, props.appointmentId]);

   return (
      <Tooltip
         testId={'startVideoConsultation'}
         content={translationKeys.youCannotStartVideoConsultationYouHaveOngoingVideoConsultation}
         disabled={!props.disabled}
      >
         <StyledVideoConsultationStartButton
            testId={'startVideoConsultation'}
            type={'button'}
            onClick={handleClick}
            disabled={Boolean(props.disabled)}
            label={
               <StyledFlex gap={8} alignItems={'center'}>
                  <IconWrapper testId={'videoProfile'} size={IconSize.NORMAL} fillColor={IconFillColor.INHERIT}>
                     <VideoProfileIcon />
                  </IconWrapper>
                  <Translate>{props.label}</Translate>
               </StyledFlex>
            }
         />
      </Tooltip>
   );
};
